import React, { useState, useMemo } from 'react';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import EmailForm from './EmailForm';
import ReplyCard from './ReplyCard';
import { setCurrentMail } from '../../../Slices/notification';

const EmailThread = ({ replies, handleFetchData, userEmail, client, EmailList, salesInfo, onDeleteReply, onMarkUnreadReply, onFlagReply, originalEmailId }) => {
    const dispatch = useDispatch();
    const [replyData, setReplyData] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [forwardData, setForwardData] = useState(null);

    // Oldest first so the conversation reads top-to-bottom chronologically
    const sortedReplies = useMemo(() => [...replies].reverse(), [replies]);

    let originalMsgFrom = replies[0]?.from ?? '';
    let originalMsgTo = client ? replies[0]?.from : replies[0]?.to ?? '';

    // For each message, if it's the user's own reply (not the original), show "You replied on"
    // above that message. This appears before the user's outbound reply card, not the incoming one.
    const repliedOnDates = useMemo(() => {
        return sortedReplies.map((msg, i) => {
            // Only show for the user's own messages
            if (msg.provider === 'ses-sent') return null; // skip original
            const isOwn =
                msg.provider === 'ses-sent-reply' ||
                (userEmail && msg.from && (msg.from === userEmail || msg.fromName === userEmail));
            if (!isOwn) return null;
            // Only show if the previous message is from someone else (incoming)
            if (i === 0) return msg.receivedDateTime;
            const prev = sortedReplies[i - 1];
            const prevIsOwn =
                prev.provider === 'ses-sent' ||
                prev.provider === 'ses-sent-reply' ||
                (userEmail && prev.from && (prev.from === userEmail || prev.fromName === userEmail));
            return !prevIsOwn ? msg.receivedDateTime : null;
        });
    }, [sortedReplies, userEmail]);

    const handleReply = (reply) => {
        if (userEmail) {
            originalMsgTo = reply.from === userEmail ? reply.to : reply.from;
            originalMsgFrom = userEmail;
        } else if (client) {
            originalMsgFrom = client;
        }
        dispatch(setCurrentMail(originalMsgFrom));
        setReplyData({
            messageId: originalEmailId || reply.id,
            to: originalMsgTo,
            subject: reply.subject,
            body: reply.bodyPreview,
            from: originalMsgFrom,
            date: reply.receivedDateTime,
            attachments: reply.attachments,
        });
        setIsModalVisible(true);
    };

    const handleReplyAll = (reply) => {
        const from = userEmail || client || originalMsgFrom;
        // Standard Gmail/Outlook Reply All:
        // TO: original sender only
        const senderAddr = reply.from || '';
        const toAll = senderAddr && senderAddr !== from ? senderAddr : '';
        // CC: (original TO + original CC) excluding self and the sender
        const ccAddrs = [
            ...(reply.to ? reply.to.split(',').map(s => s.trim()) : []),
            ...(reply.ccRecipients ? reply.ccRecipients.split(',').map(s => s.trim()) : []),
        ];
        const ccAll = [...new Set(ccAddrs.filter(a => a && a !== from && a !== senderAddr))].join(', ');
        dispatch(setCurrentMail(from));
        setReplyData({
            messageId: originalEmailId || reply.id,
            to: toAll,
            cc: ccAll,
            subject: reply.subject,
            body: reply.bodyPreview,
            from,
            date: reply.receivedDateTime,
            attachments: reply.attachments,
        });
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setReplyData(null);
        setForwardData(null);
    };

    const handleForward = (reply) => {
        setForwardData({
            subject: 'Fwd: ' + reply.subject,
            body: reply.bodyPreview,
            from: client ? client : originalMsgFrom,
            date: reply.receivedDateTime,
            attachments: reply.attachments,
            messageId: reply.id,
        });
        setIsModalVisible(true);
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ minHeight: 400, maxHeight: '80vh', overflowY: 'auto', paddingRight: 10 }}>
                {sortedReplies.map((reply, index) => (
                    <ReplyCard
                        key={reply.id}
                        reply={reply}
                        index={index}
                        minimized={false}
                        isLast={index === sortedReplies.length - 1}
                        repliedOnDate={repliedOnDates[index]}
                        userEmail={userEmail || client}
                        onReplyClick={handleReply}
                        onReplyAllClick={handleReplyAll}
                        onForwardClick={handleForward}
                        onDelete={onDeleteReply}
                        onMarkUnread={onMarkUnreadReply}
                        onFlag={onFlagReply}
                    />
                ))}

                <Modal
                    title={replyData ? 'Reply to Email' : forwardData ? 'Forward Mail' : ''}
                    open={isModalVisible}
                    onCancel={handleCloseModal}
                    footer={null}
                    width="90%"
                    zIndex={2000}
                    destroyOnClose
                >
                    {replyData && (
                        <EmailForm
                            EmailList={EmailList}
                            replyData={replyData}
                            handleCloseModal={handleCloseModal}
                            handleFetchData={handleFetchData}
                            salesInfo={salesInfo}
                        />
                    )}
                    {forwardData && (
                        <EmailForm
                            EmailList={EmailList}
                            forwardData={forwardData}
                            handleCloseModal={handleCloseModal}
                            handleFetchData={handleFetchData}
                            salesInfo={salesInfo}
                        />
                    )}
                </Modal>
            </div>
        </div>
    );
};

export default EmailThread;
